import { FC, useMemo } from 'react'

import {
  CampaignStatsTopKeyword,
  KeywordFetchKeywordsWithStats,
} from 'src/services/__generated__/api'

import { TopNegativeKeywordCard } from './TopNegativeKeywordCard'
import { TopNeutralKeywordCard } from './TopNeutralKeywordCard'
import { TopPositiveKeywordCard } from './TopPositiveKeywordCard'
import { TotalKeywordsCard } from './TotalKeywordsCard'
import { TotalMentionsCard } from './TotalMentionsCard'
import { TotalSourcesCard } from './TotalSourcesCard'
import styles from './keywordsHeroSection.module.scss'

interface KeywordsHeroSectionProps {
  activeKeywords: number
  isLifetime?: boolean
  isLoading: boolean
  keywordStats?: KeywordFetchKeywordsWithStats[]
  negativePercentage: number
  neutralPercentage: number
  positivePercentage: number
  topKeywords: CampaignStatsTopKeyword[]
  totalKeywords: number
  totalMentions: number
}

export const KeywordsHeroSection: FC<KeywordsHeroSectionProps> = ({
  activeKeywords,
  isLifetime = false,
  isLoading,
  keywordStats = [],
  negativePercentage,
  neutralPercentage,
  positivePercentage,
  topKeywords,
  totalKeywords,
  totalMentions,
}) => {
  const topPositive = useMemo(
    () =>
      [...keywordStats].sort(
        (a, b) => (b.positiveCount || 0) - (a.positiveCount || 0)
      )[0],
    [keywordStats]
  )
  const topNeutral = useMemo(
    () =>
      [...keywordStats].sort(
        (a, b) => (b.neutralCount || 0) - (a.neutralCount || 0)
      )[0],
    [keywordStats]
  )
  const topNegative = useMemo(
    () =>
      [...keywordStats].sort(
        (a, b) => (b.negativeCount || 0) - (a.negativeCount || 0)
      )[0],
    [keywordStats]
  )

  return (
    <div className={styles.wrapper}>
      <div className={styles.cards}>
        <TotalKeywordsCard
          activeCount={activeKeywords}
          hasData={isLoading || totalKeywords > 0}
          totalCount={totalKeywords}
        />
        <TotalMentionsCard
          count={totalMentions}
          hasData={isLoading || totalMentions > 0}
          negativePercentage={negativePercentage}
          neutralPercentage={neutralPercentage}
          positivePercentage={positivePercentage}
        />
        <TotalSourcesCard
          hasData={isLoading || topKeywords.length > 0}
          topKeywords={topKeywords}
        />
        <TopPositiveKeywordCard
          hasData={isLoading || (topPositive?.positiveCount || 0) > 0}
          keyword={topPositive}
          percentage={
            isLifetime ? undefined : topPositive?.positivityPercentage
          }
        />
        <TopNeutralKeywordCard
          hasData={isLoading || (topNeutral?.neutralCount || 0) > 0}
          keyword={topNeutral}
          percentage={isLifetime ? undefined : topNeutral?.neutralityPercentage}
        />
        <TopNegativeKeywordCard
          hasData={isLoading || (topNegative?.negativeCount || 0) > 0}
          keyword={topNegative}
          percentage={
            isLifetime ? undefined : topNegative?.negativityPercentage
          }
        />
      </div>
    </div>
  )
}
